import { ActionIcon, Button, Modal, Textarea, Title, rem } from "@mantine/core";
import IconPlus from "@src/assets/icons/lucide/plus.svg?react";
import IconBug from "@src/assets/icons/tabler/bug.svg?react";
import IconX from "@src/assets/icons/tabler/x.svg?react";
import { UserProfileContext } from "@src/components/user/user-context";
import config from "@src/config";
import {
	showErrorNotification,
	showSuccessNotification,
} from "@src/utils/notifications";
import { clsx } from "clsx";
import { usePostHog } from "posthog-js/react";
import {
	type ChangeEvent,
	type ClipboardEvent,
	type DragEvent,
	useCallback,
	useContext,
	useEffect,
	useRef,
	useState,
} from "react";
import {
	type DefectOpenSnapshot,
	captureDefectOpenSnapshot,
	submitDefectReport,
} from "./submit-defect-report";

interface DefectReporterModalProps {
	opened: boolean;
	onClose: () => void;
}

function formatFileSize(bytes: number): string {
	if (bytes < 1024) {
		return `${bytes} B`;
	}
	if (bytes < 1024 * 1024) {
		return `${Math.round(bytes / 1024)} KB`;
	}
	return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function DefectReporterModal({
	opened,
	onClose,
}: DefectReporterModalProps) {
	const posthog = usePostHog();
	const profileContext = useContext(UserProfileContext);
	const currentUser = profileContext?.user[0] ?? null;

	const [note, setNote] = useState("");
	const [files, setFiles] = useState<File[]>([]);
	const [isDraggingOver, setIsDraggingOver] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const fileInputRef = useRef<HTMLInputElement>(null);
	const openSnapshotRef = useRef<DefectOpenSnapshot | null>(null);

	useEffect(() => {
		if (opened) {
			if (!openSnapshotRef.current) {
				openSnapshotRef.current = captureDefectOpenSnapshot(posthog);
			}
		} else {
			openSnapshotRef.current = null;
		}
	}, [opened, posthog]);

	const appendFiles = useCallback((incoming: FileList | File[]) => {
		const valid = Array.from(incoming).filter(
			(file) =>
				file.type.startsWith("image/") || file.type.startsWith("video/"),
		);
		if (valid.length === 0) {
			return;
		}
		setFiles((prev) => [...prev, ...valid]);
	}, []);

	const handleFileChange = useCallback(
		(event: ChangeEvent<HTMLInputElement>) => {
			if (event.target.files) {
				appendFiles(event.target.files);
			}
			event.target.value = "";
		},
		[appendFiles],
	);

	const handlePaste = useCallback(
		(event: ClipboardEvent<HTMLTextAreaElement>) => {
			if (event.clipboardData?.files && event.clipboardData.files.length > 0) {
				appendFiles(event.clipboardData.files);
			}
		},
		[appendFiles],
	);

	const handleDrop = useCallback(
		(event: DragEvent<HTMLElement>) => {
			event.preventDefault();
			event.stopPropagation();
			setIsDraggingOver(false);
			if (event.dataTransfer?.files && event.dataTransfer.files.length > 0) {
				appendFiles(event.dataTransfer.files);
			}
		},
		[appendFiles],
	);

	const removeFileAt = useCallback((index: number) => {
		setFiles((prev) => prev.filter((_, idx) => idx !== index));
	}, []);

	const handleClose = useCallback(() => {
		if (isSubmitting) return;
		setNote("");
		setFiles([]);
		openSnapshotRef.current = null;
		onClose();
	}, [isSubmitting, onClose]);

	const handleSubmit = useCallback(async () => {
		if (isSubmitting) return;
		setIsSubmitting(true);
		try {
			const result = await submitDefectReport({
				note,
				files,
				openSnapshot: openSnapshotRef.current,
				user: currentUser
					? {
							id: currentUser.id,
							email: currentUser.email,
							displayName: currentUser.displayName,
						}
					: null,
				posthog,
			});

			const shortRef = result.payload.reportId.slice(0, 8);
			showSuccessNotification(
				"Bug report sent",
				<span>
					Ref #{shortRef} ·{" "}
					<a
						href={config.discordDefectsUrl}
						target="_blank"
						rel="noopener noreferrer"
						className="font-medium text-brand-primary underline hover:opacity-80"
					>
						#defects on Discord
					</a>
				</span>,
			);
			setNote("");
			setFiles([]);
			openSnapshotRef.current = null;
			onClose();
		} catch {
			showErrorNotification(
				"Could not send report",
				"Something went wrong while sending your report. Please try again.",
			);
		} finally {
			setIsSubmitting(false);
		}
	}, [currentUser, files, isSubmitting, note, onClose, posthog]);

	return (
		<Modal
			opened={opened}
			onClose={handleClose}
			radius="md"
			padding={rem(24)}
			withCloseButton={false}
			centered
			zIndex={2000}
		>
			<div onDragOver={(e) => e.preventDefault()} onDrop={handleDrop}>
				<div className="mb-4 flex items-center justify-between">
					<div className="flex items-center gap-2">
						<div className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-primary-soft text-brand-primary">
							<IconBug className="h-4 w-4" />
						</div>
						<Title order={4}>Report a bug</Title>
					</div>
					<ActionIcon
						variant="subtle"
						color="var(--ui-color-text-primary)"
						size="sm"
						onClick={handleClose}
						aria-label="Close bug report modal"
						title="Close"
					>
						<IconX className="h-4 w-4" />
					</ActionIcon>
				</div>

				<Textarea
					label="What happened?"
					value={note}
					onChange={(event) => setNote(event.currentTarget.value)}
					onPaste={handlePaste}
					placeholder="Describe what went wrong, or paste a screenshot (optional)..."
					minRows={3}
					maxRows={6}
					autosize
					radius="md"
					aria-label="What happened?"
					styles={{
						input: {
							borderColor: "var(--ui-color-border-default)",
							backgroundColor: "var(--ui-color-surface-subtle)",
						},
						label: {
							marginBottom: rem(6),
							color: "var(--ui-color-text-primary)",
						},
					}}
					onKeyDown={(event) => {
						if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
							event.preventDefault();
							handleSubmit();
						}
					}}
				/>

				<div className="mt-3">
					<input
						ref={fileInputRef}
						type="file"
						accept="image/*,video/*"
						multiple
						onChange={handleFileChange}
						className="hidden"
						data-testid="defect-attachment-input"
					/>
					<button
						type="button"
						onClick={() => fileInputRef.current?.click()}
						onDragEnter={(event) => {
							event.preventDefault();
							setIsDraggingOver(true);
						}}
						onDragOver={(event) => {
							event.preventDefault();
							setIsDraggingOver(true);
						}}
						onDragLeave={() => setIsDraggingOver(false)}
						onDrop={handleDrop}
						data-testid="attach-media-button"
						aria-label="Upload screenshot or video"
						title="Upload screenshot or video"
						className={clsx(
							"flex w-full flex-col items-center justify-center gap-2",
							"rounded-xl border border-dashed px-4 py-5 text-center",
							"transition-colors cursor-pointer",
							isDraggingOver
								? "border-brand-primary bg-brand-primary-soft text-brand-primary"
								: [
										"border-border-default bg-surface-subtle/50",
										"text-text-secondary",
										"hover:border-brand-primary hover:bg-brand-primary-soft/60",
										"hover:text-text-primary",
									],
						)}
					>
						<div
							className={clsx(
								"flex h-9 w-9 items-center justify-center rounded-full",
								"border border-border-default bg-surface-default",
								"text-text-primary",
							)}
						>
							<IconPlus className="h-4 w-4" />
						</div>
						<span className="text-xs font-medium">
							Drop screenshot or video here, or click to upload
						</span>
					</button>

					{files.length > 0 && (
						<div className="mt-2 flex flex-wrap gap-1.5">
							{files.map((file, index) => (
								<div
									key={`${file.name}-${file.size}-${index}`}
									className={clsx(
										"inline-flex items-center gap-1.5 rounded-md",
										"border border-border-subtle bg-surface-subtle",
										"px-2 py-1 text-xs text-text-primary",
									)}
								>
									<span className="max-w-[180px] truncate">{file.name}</span>
									<span className="text-text-secondary">
										({formatFileSize(file.size)})
									</span>
									<button
										type="button"
										onClick={() => removeFileAt(index)}
										aria-label={`Remove ${file.name}`}
										title="Remove attachment"
										className="text-text-secondary hover:text-text-primary"
									>
										<IconX className="h-3 w-3" />
									</button>
								</div>
							))}
						</div>
					)}
				</div>

				<p className="mt-3 text-[11px] leading-relaxed text-text-tertiary">
					Recent activity from the last 60 seconds will be included
					automatically for better context.
				</p>

				<div className="mt-5 flex items-center justify-end gap-2">
					<Button
						variant="outline"
						size="sm"
						radius="md"
						onClick={handleClose}
						disabled={isSubmitting}
						className={clsx(
							"text-text-primary bg-surface-default border-border-default",
							"hover:text-brand-primary hover:bg-surface-hover-subtle",
						)}
					>
						Cancel
					</Button>
					<Button
						variant="cta"
						size="sm"
						radius="md"
						onClick={handleSubmit}
						loading={isSubmitting}
						data-testid="submit-defect-report-button"
					>
						Send report
					</Button>
				</div>
			</div>
		</Modal>
	);
}
